/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { showSuccessToast, showErrorToast, showLoadingToast, dismissAllToasts } from "@/utils/toast";
import { useAuthTokens } from "@/hooks/useAuthTokens";
import { posthog, identifyUser, resetUser, setUserPersonProperties } from "@/lib/posthog";
import { isPhoneNumber, normalizePhone } from "@/utils/phone";

export type SignupSource = 'b2b' | 'd2c' | 'nfc' | 'portal' | 'partner' | 'oauth_google' | 'unknown';

// Heuristic for OAuth signups: SIGNED_IN fires for both fresh signup and returning login.
// A fresh user has created_at within seconds of now. 60s gives generous slack for slow OAuth handshakes.
const OAUTH_FRESH_SIGNUP_WINDOW_MS = 60_000;

function captureSignupOnce(userId: string, properties: Record<string, unknown>) {
  const key = `signup_completed:${userId}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  posthog.capture('signup_completed', properties);
}

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  inPasswordRecovery: boolean;
  signIn: (identifier: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (phone: string, email: string, password: string, firstName: string, lastName: string, source?: SignupSource) => Promise<{ user: User | null; error: Error | null }>;
  signOut: (redirectTo?: string) => Promise<void>;
  ensureAdminRecord: (userId: string, email: string, firstName: string, lastName: string, phone?: string) => Promise<boolean>;
  redeemToken: (token: string, memorialPurpose: 'self' | 'loved_one') => Promise<{ success: boolean; message: string; quantity?: number; memorialId?: string }>;
  setInPasswordRecovery: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Best-effort enrichment so PostHog funnels can be segmented by the inviting
// monument company. Picks the user's most recent memorial; fire-and-forget,
// does not block auth.
const enrichPostHogWithMonumentCompany = (userId: string) => {
  supabase
    .from('memorials')
    .select('monument_company_id')
    .eq('user_id', userId)
    .not('monument_company_id', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
    .then(({ data }) => {
      if (data?.monument_company_id) {
        setUserPersonProperties({ monument_company_id: data.monument_company_id });
      }
    })
    .catch(() => { /* non-critical enrichment */ });
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inPasswordRecovery, setInPasswordRecovery] = useState(false);
  const {
    redeemToken: redeemTokenHelper,
    claimPendingTokensByEmail: claimPendingTokensHelper,
    claimPendingTokensByPhone: claimPendingTokensByPhoneHelper
  } = useAuthTokens();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error getting session:", error);
          clearAuthData();
        } else {
          setSession(data.session);
          setUser(data.session?.user || null);
          if (data.session?.user) {
            identifyUser({ id: data.session.user.id, email: data.session.user.email, phone: data.session.user.phone });
            enrichPostHogWithMonumentCompany(data.session.user.id);
          }
        }
      } catch (error) {
        console.error("Unexpected error during auth initialization:", error);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        setInPasswordRecovery(true);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setSession(currentSession);
        setUser(currentSession?.user || null);
        if (currentSession?.user) {
          identifyUser({ id: currentSession.user.id, email: currentSession.user.email, phone: currentSession.user.phone });

          // Auto-link pending invitations and claim memorials on sign-in
          if (event === 'SIGNED_IN') {
            enrichPostHogWithMonumentCompany(currentSession.user.id);

            // OAuth signups don't flow through signUp() — detect a fresh OAuth user here.
            // captureSignupOnce + sessionStorage prevents duplicates if SIGNED_IN re-fires.
            const provider = currentSession.user.app_metadata?.provider;
            const createdAt = currentSession.user.created_at ? new Date(currentSession.user.created_at).getTime() : 0;
            const isFreshOAuth = provider === 'google' && createdAt > 0 && (Date.now() - createdAt) < OAUTH_FRESH_SIGNUP_WINDOW_MS;
            if (isFreshOAuth) {
              captureSignupOnce(currentSession.user.id, {
                source: 'oauth_google' satisfies SignupSource,
                signup_method: 'oauth',
                claimed_token_count: 0,
              });
            }

            // Link phone-based collaborator invitations
            if (currentSession.user.phone) {
              supabase.rpc('link_pending_collaborator_invitations_by_phone', {
                _user_id: currentSession.user.id,
                _phone: currentSession.user.phone,
              }).then(({ data: linkedCount }) => {
                if (linkedCount && linkedCount > 0) {
                  showSuccessToast(`You have access to ${linkedCount} memorial${linkedCount > 1 ? 's' : ''} shared with you!`);
                }
              }).catch((err) => {
                console.error('Error linking phone invitations:', err);
              });

              // Claim phone-assigned B2B memorials
              claimPendingTokensByPhoneHelper(currentSession.user.phone, currentSession.user.id)
                .catch((err) => console.error('Error claiming phone tokens:', err));
            }

            // Claim email-assigned B2B memorials
            if (currentSession.user.email) {
              claimPendingTokensHelper(currentSession.user.email, currentSession.user.id)
                .catch((err) => console.error('Error claiming email tokens:', err));
            }
          }
        }
      } else if (event === 'SIGNED_OUT') {
        clearAuthData();

        // Use window.location for navigation to avoid useNavigate dependency
        if (!inPasswordRecovery && !isPublicRoute(window.location.pathname)) {
          window.location.href = '/auth';
        }
      } else if (event === 'USER_UPDATED') {
        setSession(currentSession);
        setUser(currentSession?.user || null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [inPasswordRecovery]);

  const isPublicRoute = (path: string) => {
    return path === '/reset-password' || path.startsWith('/memorial/') || path.startsWith('/activate/') || path.startsWith('/welcome/');
  };

  const clearAuthData = () => {
    setSession(null);
    setUser(null);
    resetUser();
    
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('supabase.') || key.includes('supabase')) {
        localStorage.removeItem(key);
      }
    }
    
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('supabase.') || key.includes('supabase')) {
        sessionStorage.removeItem(key);
      }
    }
    
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      if (name.includes('supabase')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  };

  const claimPendingTokensByEmail = async (email: string, userId: string) => {
    const result = await claimPendingTokensHelper(email, userId);
    return result;
  };

  const signIn = async (identifier: string, password: string) => {
    try {
      const credentials = isPhoneNumber(identifier)
        ? { phone: normalizePhone(identifier), password }
        : { email: identifier, password };

      const { error } = await supabase.auth.signInWithPassword(credentials);

      if (error) throw error;
      showSuccessToast("Successfully signed in!");
    } catch (error: unknown) {
      showErrorToast(error instanceof Error ? error.message : "Error signing in");
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
    } catch (error: unknown) {
      showErrorToast(error instanceof Error ? error.message : "Error signing in with Google");
      throw error;
    }
  };

  const signUp = async (phone: string, email: string, password: string, firstName: string, lastName: string, source: SignupSource = 'unknown') => {
    try {
      const normalizedPhone = normalizePhone(phone);

      // Sign up with email as primary identifier (avoids "confirm new email" issue)
      // Phone is attached after via updateUser — phone confirmations are disabled
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        },
      });

      if (error) throw error;

      // Supabase returns empty identities for existing emails (enumeration protection)
      if (data.user?.identities?.length === 0) {
        throw new Error("An account with this email already exists. Please sign in instead.");
      }

      if (data.user) {
        // Attach phone to the account (for phone-based login + collaboration invites)
        const { error: updateError } = await supabase.auth.updateUser({ phone: normalizedPhone });
        if (updateError) {
          console.error('Error attaching phone to account:', updateError);
          // Non-fatal — account is created, phone can be added later
        }

        // Link pending phone-based collaborator invitations
        try {
          await supabase.rpc('link_pending_collaborator_invitations_by_phone', {
            _user_id: data.user.id,
            _phone: normalizedPhone,
          });
        } catch (linkError) {
          console.error('Error linking phone invitations:', linkError);
        }

        // Claim pending tokens by email and phone
        const tokenResult = await claimPendingTokensByEmail(email, data.user.id);
        const phoneTokenResult = await claimPendingTokensByPhoneHelper(normalizedPhone, data.user.id);
        const totalClaimed = (tokenResult.claimed || 0) + (phoneTokenResult.claimed || 0);
        if (totalClaimed > 0) {
          showSuccessToast(`Successfully claimed ${totalClaimed} memorial medallion${totalClaimed > 1 ? 's' : ''}!`);
        }

        // Merge the anonymous pre-signup distinct_id into the real user.id so any
        // /activate pageview, ?email= identification, etc. attach to the same PostHog person.
        posthog.alias(data.user.id);
        identifyUser({ id: data.user.id, email: data.user.email, phone: data.user.phone });
        captureSignupOnce(data.user.id, {
          source,
          signup_method: 'email',
          claimed_token_count: totalClaimed,
        });
      }

      showSuccessToast("Successfully signed up!");
      return { user: data.user, error: null };
    } catch (error: unknown) {
      console.error('Sign up error:', error);
      showErrorToast(error instanceof Error ? error.message : "Error signing up");
      return { user: null, error: error instanceof Error ? error : new Error("Error signing up") };
    }
  };

  const signOut = async (redirectTo: string = '/auth') => {
    try {
      const loadingToastId = showLoadingToast("Logging out...");

      // Sign out from Supabase FIRST
      const { error } = await supabase.auth.signOut({
        scope: 'global'
      });

      // Dismiss the loading toast
      dismissAllToasts();

      if (error) {
        console.error('Sign out error from Supabase:', error);
        showErrorToast("Error logging out, but clearing local data anyway");
      } else {
        showSuccessToast("Successfully logged out!");
      }

      // Clear local auth data as cleanup
      clearAuthData();

      if (!inPasswordRecovery) {
        window.location.href = redirectTo;
      }

    } catch (error: unknown) {
      console.error('Unexpected logout error:', error);

      // Dismiss loading toast and show error
      dismissAllToasts();
      showErrorToast("Error logging out");

      // Force cleanup and redirect even on error
      clearAuthData();

      if (!inPasswordRecovery) {
        window.location.href = redirectTo;
      }
    }
  };

  const redeemToken = async (
    token: string,
    memorialPurpose: 'self' | 'loved_one'
  ): Promise<{ success: boolean; message: string; quantity?: number; memorialId?: string }> => {
    if (!user) {
      return { success: false, message: "You must be logged in to redeem a token" };
    }
    const result = await redeemTokenHelper(token, user.id, memorialPurpose);
    return result;
  };

  const ensureAdminRecord = async (userId: string, email: string, firstName: string, lastName: string, phone?: string) => {
    try {
      const { data: existingAdmin, error: checkError } = await supabase
        .from('admins')
        .select('*')
        .eq('id', userId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking admin existence:', checkError);
        return false;
      }

      if (existingAdmin) {
        // Update phone if it's missing and we have one now
        if (phone && !existingAdmin.phone) {
          await supabase.from('admins').update({ phone }).eq('id', userId);
        }
        return true;
      }

      const { error: insertError } = await supabase
        .from('admins')
        .insert({
          id: userId,
          email: email,
          first_name: firstName,
          last_name: lastName,
          phone: phone || null,
          active: true
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating admin record:', insertError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Unexpected error in ensureAdminRecord:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isLoading,
        isAuthenticated: !!session,
        inPasswordRecovery,
        setInPasswordRecovery,
        signIn,
        signInWithGoogle,
        signUp,
        signOut,
        ensureAdminRecord,
        redeemToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
